import { Fragment, useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import PageHeader from '../../components/PageHeader';
import Button from '../../components/ui/Button';
import { DollarSign, ArrowLeft, Check, Plus, Trash2, Lock } from 'lucide-react';
import nurseryService from '../../services/nursery';
import { useModuleLicense } from '../../hooks/useModuleLicense';
import { errorMessage } from '../../utils/apiResponse';
import { groupByCrop, NO_CROP, productCrop } from '../../utils/cropGroups';
import type {
  NurseryCustomerTier, NurseryStockItem, CreatePriceListPayload,
  NurseryPriceListStatus, PriceListItemPayload, NurseryProduct,
} from '../../types/nursery';

// A row prices a stock item ("s:5") or an umbrella product ("p:3").
interface ItemRow {
  item: string;
  min_quantity: number;
  unit_price: number;
}

function itemPayload(key: string): Pick<PriceListItemPayload, 'stock_item_id' | 'product_id'> | null {
  const m = /^([sp]):(\d+)$/.exec(key);
  if (!m) return null;
  return m[1] === 'p' ? { product_id: Number(m[2]) } : { stock_item_id: Number(m[2]) };
}

/** The crop each priceable thing is listed under, by row key ("s:5", "p:3"). */
function cropsByKey(stockItems: NurseryStockItem[], products: NurseryProduct[]): Map<string, string> {
  const crops = new Map<string, string>();
  for (const si of stockItems) crops.set(`s:${si.id}`, si.crop_type_name?.trim() || NO_CROP);
  for (const p of products) crops.set(`p:${p.id}`, productCrop(p) || NO_CROP);
  return crops;
}

const stockLabel = (si: NurseryStockItem) =>
  si.kind === 'merchandise'
    ? `${si.name}${si.unit ? ` (${si.unit})` : ''}`
    : `${si.cultivar_name}${si.rootstock_name ? ` on ${si.rootstock_name}` : ''}${si.tree_type_code ? ` (${si.tree_type_code})` : ''}`;
const productLabel = (p: NurseryProduct) =>
  `${[p.name, p.tree_type_name || p.tree_type_code].filter(Boolean).join(' · ')}${p.active ? '' : ' (retired)'}`;

function todayStr(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function PriceListForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);
  // An accounts seat, or a Retail manager: a shop sets its own prices.
  const accounts = useModuleLicense('accounts', ['user', 'admin']);
  const shop = useModuleLicense('retail', 'manager');
  const hasAccess = accounts.hasAccess || shop.hasAccess;
  const licenseLoading = accounts.loading || shop.loading;

  const [tiers, setTiers] = useState<NurseryCustomerTier[]>([]);
  const [stockItems, setStockItems] = useState<NurseryStockItem[]>([]);
  const [products, setProducts] = useState<NurseryProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [name, setName] = useState('');
  const [tierId, setTierId] = useState<number | ''>('');
  const [status, setStatus] = useState<NurseryPriceListStatus>('draft');
  const [effectiveFrom, setEffectiveFrom] = useState(todayStr());
  const [effectiveTo, setEffectiveTo] = useState('');
  const [notes, setNotes] = useState('');
  const [items, setItems] = useState<ItemRow[]>([]);
  const [cropFilter, setCropFilter] = useState('all');

  useEffect(() => {
    const init = async () => {
      const [tierRes, stockRes, productRes] = await Promise.all([
        nurseryService.getCustomerTiers(),
        nurseryService.getStockItems({ active: true }),
        nurseryService.getProducts(),
      ]);
      if (tierRes.success) setTiers(tierRes.data || []);
      if (stockRes.success) setStockItems(stockRes.data || []);
      if (productRes.success) setProducts(productRes.data || []);

      if (isEdit) {
        const res = await nurseryService.getPriceList(Number(id));
        if (res.success && res.data) {
          const pl = res.data;
          setName(pl.name);
          setTierId(pl.customer_tier_id ?? '');
          setStatus(pl.status);
          setEffectiveFrom(pl.effective_from);
          setEffectiveTo(pl.effective_to ?? '');
          setNotes(pl.notes ?? '');
          // Sorted by crop once, as the list opens. A row never moves while it is being edited.
          const stock = (stockRes.success && stockRes.data) || [];
          const prods = (productRes.success && productRes.data) || [];
          const crops = cropsByKey(stock, prods);
          const labels = new Map<string, string>([
            ...stock.map((si): [string, string] => [`s:${si.id}`, stockLabel(si)]),
            ...prods.map((p): [string, string] => [`p:${p.id}`, productLabel(p)]),
          ]);
          const rows = (pl.items || []).map(i => ({
            item: i.product_id ? `p:${i.product_id}` : `s:${i.stock_item_id}`,
            min_quantity: i.min_quantity,
            unit_price: Number(i.unit_price),
          }));
          setItems(groupByCrop(
            rows,
            (r) => crops.get(r.item),
            (a, b) => (labels.get(a.item) ?? '').localeCompare(labels.get(b.item) ?? '') || a.min_quantity - b.min_quantity,
          ).flatMap(g => g.items));
        }
      }
      setLoading(false);
    };
    init();
  }, [id, isEdit]);

  const addItem = () => setItems(prev => [...prev, { item: '', min_quantity: 1, unit_price: 0 }]);
  const removeItem = (idx: number) => setItems(prev => prev.filter((_, i) => i !== idx));
  const updateItem = (idx: number, patch: Partial<ItemRow>) =>
    setItems(prev => prev.map((it, i) => (i === idx ? { ...it, ...patch } : it)));

  const onSubmit = async () => {
    setError(null);
    if (!name.trim()) { setError('Name is required'); return; }
    const validItems = items.filter(i => itemPayload(i.item));
    setSubmitting(true);
    try {
      const payload: CreatePriceListPayload = {
        name: name.trim(),
        currency: 'AUD',
        effective_from: effectiveFrom,
        status,
        ...(tierId ? { customer_tier_id: Number(tierId) } : { customer_tier_id: null }),
        ...(effectiveTo ? { effective_to: effectiveTo } : { effective_to: null }),
        ...(notes ? { notes } : {}),
        items: validItems.map<PriceListItemPayload>(i => ({
          ...itemPayload(i.item),
          min_quantity: i.min_quantity || 1,
          unit_price: i.unit_price || 0,
        })),
      };
      const res = isEdit
        ? await nurseryService.updatePriceList(Number(id), payload)
        : await nurseryService.createPriceList(payload);
      if (res.success) {
        navigate('/accounts/price-lists');
      } else {
        setError(errorMessage(res, 'Failed to save price list'));
      }
    } catch (e) {
      setError(errorMessage(e, 'Failed to save price list'));
    } finally {
      setSubmitting(false);
    }
  };

  if (licenseLoading || loading) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-6">
        <div className="h-8 bg-neutral-100 rounded animate-pulse w-64 mb-4" />
        <div className="h-64 bg-neutral-100 rounded animate-pulse" />
      </div>
    );
  }

  if (!hasAccess) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-12">
        <div className="bg-white border border-neutral-200 rounded-xl p-8 text-center">
          <Lock className="h-10 w-10 text-neutral-300 mx-auto mb-3" />
          <h2 className="text-lg font-semibold text-neutral-800 mb-1">Accounts licence required</h2>
          <p className="text-sm text-neutral-500 mb-4">
            Managing price lists requires an active <span className="font-medium">Accounts</span> licence seat.
          </p>
          <Button variant="outline" size="sm" onClick={() => navigate('/accounts/price-lists')}>
            <ArrowLeft className="h-4 w-4 mr-1" /> Back to price lists
          </Button>
        </div>
      </div>
    );
  }

  const inputCls = 'w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500';

  // Crop by crop: the picker's options, and a heading wherever the rows change crop.
  const crops = cropsByKey(stockItems, products);
  const hasCrops = [...crops.values()].some(c => c !== NO_CROP);
  const optionGroups = groupByCrop<{ key: string; label: string; crop: string; product: boolean }>(
    [
      ...products.map(p => ({ key: `p:${p.id}`, label: productLabel(p), crop: crops.get(`p:${p.id}`) ?? NO_CROP, product: true })),
      ...stockItems.map(si => ({ key: `s:${si.id}`, label: stockLabel(si), crop: crops.get(`s:${si.id}`) ?? NO_CROP, product: false })),
    ],
    (o) => o.crop,
    (a, b) => Number(b.product) - Number(a.product) || a.label.localeCompare(b.label),
  );
  const rowCrop = (it: ItemRow) => (it.item ? crops.get(it.item) ?? NO_CROP : null);
  const rowCrops = groupByCrop(items.filter(it => it.item), rowCrop, () => 0).map(g => g.crop);
  const cropShown = rowCrops.includes(cropFilter) ? cropFilter : 'all';
  // A row with nothing chosen yet is always shown: it is the one being added.
  const shownRows = items
    .map((it, idx) => ({ it, idx, crop: rowCrop(it) }))
    .filter(r => cropShown === 'all' || r.crop == null || r.crop === cropShown);

  return (
    <>
      <PageHeader
        title={isEdit ? 'Edit Price List' : 'New Price List'}
        subtitle="Prices with volume breaks. Customers are put on a list from their own page"
        icon={<DollarSign className="h-6 w-6 text-white" />}
      />
      <div className="max-w-3xl mx-auto px-4 py-6">
        <div className="space-y-6">
          <div className="bg-white border border-neutral-200 rounded-xl p-4 sm:p-6 space-y-5">
            <div>
              <label htmlFor="pl-name" className="block text-sm font-medium text-neutral-700 mb-1">Name <span className="text-red-500">*</span></label>
              <input id="pl-name" value={name} onChange={e => setName(e.target.value)} className={inputCls} placeholder="e.g. 2026 Wholesale Bareroot" />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label htmlFor="pl-tier" className="block text-sm font-medium text-neutral-700 mb-1">Website tier</label>
                <select id="pl-tier" value={tierId} onChange={e => setTierId(e.target.value ? Number(e.target.value) : '')} className={inputCls}>
                  <option value="">None</option>
                  {tiers.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
                <p className="text-xs text-neutral-500 mt-1">Prices website shoppers at that tier. Customers you invoice use the list chosen for them.</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Status</label>
                <select value={status} onChange={e => setStatus(e.target.value as NurseryPriceListStatus)} className={inputCls}>
                  <option value="draft">Draft</option>
                  <option value="active">Active</option>
                  <option value="archived">Archived</option>
                </select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Effective From <span className="text-red-500">*</span></label>
                <input type="date" value={effectiveFrom} onChange={e => setEffectiveFrom(e.target.value)} className={inputCls} />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Effective To</label>
                <input type="date" value={effectiveTo} onChange={e => setEffectiveTo(e.target.value)} className={inputCls} />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">Notes</label>
              <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2} className={`${inputCls} resize-none`} />
            </div>
          </div>

          <div className="bg-white border border-neutral-200 rounded-xl p-6 space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h3 className="text-sm font-semibold text-neutral-600 uppercase tracking-wide">Prices</h3>
              <div className="flex flex-wrap items-center gap-2">
                {hasCrops && rowCrops.length > 1 && (
                  <select
                    aria-label="Crop type"
                    value={cropShown}
                    onChange={e => setCropFilter(e.target.value)}
                    className="border border-neutral-300 rounded-lg px-3 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="all">All crop types</option>
                    {rowCrops.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                )}
                <Button variant="outline" size="sm" type="button" onClick={addItem}>
                  <Plus className="h-4 w-4" /> Add Price
                </Button>
              </div>
            </div>

            {items.length === 0 ? (
              <p className="text-sm text-neutral-400">No prices yet. Add a price per product or stock item; use multiple rows with different min quantities for volume breaks.</p>
            ) : (
              <div className="space-y-2">
                <div className="grid grid-cols-12 gap-2 text-xs font-medium text-neutral-400 px-1">
                  <span className="col-span-6">Product or stock item</span>
                  <span className="col-span-2">Min Qty</span>
                  <span className="col-span-3">Unit Price</span>
                  <span className="col-span-1" />
                </div>
                {shownRows.map(({ it, idx, crop }, i) => (
                  <Fragment key={idx}>
                  {/* A heading wherever the crop changes from the row above, so it is true of the rows as they stand. */}
                  {hasCrops && crop != null && crop !== shownRows[i - 1]?.crop && (
                    <h4 className="pt-2 px-1 text-sm font-semibold text-neutral-800 break-words">{crop}</h4>
                  )}
                  <div className="grid grid-cols-12 gap-2 items-center">
                    <select
                      aria-label="Product or stock item"
                      value={it.item}
                      onChange={e => updateItem(idx, { item: e.target.value })}
                      className={`col-span-6 ${inputCls}`}
                    >
                      <option value="">Select...</option>
                      {hasCrops ? optionGroups.flatMap(g => [true, false].map(product => {
                        const options = g.items.filter(o => o.product === product);
                        return options.length > 0 && (
                          <optgroup key={`${g.crop}:${product}`} label={`${g.crop}: ${product ? 'products' : 'stock items'}`}>
                            {options.map(o => <option key={o.key} value={o.key}>{o.label}</option>)}
                          </optgroup>
                        );
                      })) : (
                        <>
                          {products.length > 0 && (
                            <optgroup label="Products">
                              {products.map(p => <option key={`p${p.id}`} value={`p:${p.id}`}>{productLabel(p)}</option>)}
                            </optgroup>
                          )}
                          <optgroup label="Stock items">
                            {stockItems.map(si => <option key={`s${si.id}`} value={`s:${si.id}`}>{stockLabel(si)}</option>)}
                          </optgroup>
                        </>
                      )}
                    </select>
                    <input
                      type="number" min="1"
                      value={it.min_quantity}
                      onChange={e => updateItem(idx, { min_quantity: Number(e.target.value) })}
                      className={`col-span-2 ${inputCls}`}
                    />
                    <input
                      type="number" min="0" step="0.01"
                      value={it.unit_price}
                      onChange={e => updateItem(idx, { unit_price: Number(e.target.value) })}
                      className={`col-span-3 ${inputCls}`}
                    />
                    <button type="button" onClick={() => removeItem(idx)} className="col-span-1 text-red-400 hover:text-red-600 flex justify-center">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                  </Fragment>
                ))}
              </div>
            )}
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700">{error}</div>
          )}

          <div className="flex justify-between">
            <Button variant="outline" type="button" onClick={() => navigate('/accounts/price-lists')}>
              <ArrowLeft className="h-4 w-4" /> Cancel
            </Button>
            <Button variant="primary" type="button" onClick={onSubmit} disabled={submitting}>
              {submitting ? 'Saving...' : isEdit ? 'Save Changes' : 'Create Price List'}
              {!submitting && <Check className="h-4 w-4" />}
            </Button>
          </div>
        </div>
      </div>
    </>
  );
}

export default PriceListForm;
